import { useEffect, useRef, useState } from "react";
import api, { apiError } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { LifeBuoy, Bot, Send, Plus, Loader2, MessageSquare } from "lucide-react";

const statusColor = (s) => ({ open: "bg-amber-500/20 text-amber-300", in_progress: "bg-blue-500/20 text-blue-300", resolved: "bg-emerald-500/20 text-emerald-300" }[s] || "bg-white/10");

function Assistant() {
  const [session, setSession] = useState(null);
  const [msgs, setMsgs] = useState([{ role: "assistant", text: "Hi! I'm QwikLogs Assist. Tell me what's going wrong — clock-in, leave, payroll, chat — and I'll walk you through it." }]);
  const [text, setText] = useState("");
  const [busy, setBusy] = useState(false);
  const end = useRef();
  useEffect(() => { end.current?.scrollIntoView({ behavior: "smooth" }); }, [msgs]);

  const send = async () => {
    const t = text.trim(); if (!t || busy) return;
    setMsgs((m) => [...m, { role: "user", text: t }]); setText(""); setBusy(true);
    try {
      const { data } = await api.post("/support/chat", { session_id: session, message: t });
      setSession(data.session_id);
      setMsgs((m) => [...m, { role: "assistant", text: data.reply }]);
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); setMsgs((m) => [...m, { role: "assistant", text: "Sorry, I couldn't respond. Please open a ticket and a human will help." }]); }
    finally { setBusy(false); }
  };

  return (
    <div className="card-emerald p-0 overflow-hidden flex flex-col h-[62vh] min-h-[420px]" data-testid="support-assistant">
      <div className="h-12 shrink-0 flex items-center gap-2 px-4 border-b border-emerald-500/10"><Bot className="w-4 h-4 text-emerald-400" /><span className="text-sm font-semibold">QwikLogs Assist</span><Badge className="bg-emerald-500/15 text-emerald-300 text-[10px]">AI</Badge></div>
      <div className="flex-1 overflow-y-auto p-4 space-y-3">
        {msgs.map((m, i) => (
          <div key={i} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}>
            <div className={`max-w-[85%] rounded-2xl px-3.5 py-2 text-sm whitespace-pre-wrap ${m.role === "user" ? "bg-emerald-500 text-[#062217]" : "bg-white/8"}`}>{m.text}</div>
          </div>
        ))}
        {busy && <div className="flex justify-start"><div className="bg-white/8 rounded-2xl px-3.5 py-2"><Loader2 className="w-4 h-4 animate-spin" /></div></div>}
        <div ref={end} />
      </div>
      <div className="shrink-0 p-3 border-t border-emerald-500/10 flex items-end gap-2">
        <Textarea value={text} onChange={(e) => setText(e.target.value)} onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); send(); } }} rows={1} placeholder="Describe your issue…" className="bg-[#0A0D0C] border-emerald-500/20 resize-none min-h-[42px] max-h-32" data-testid="assistant-input" />
        <Button onClick={send} disabled={busy || !text.trim()} className="bg-emerald-500 text-[#062217] shrink-0" data-testid="assistant-send-btn"><Send className="w-4 h-4" /></Button>
      </div>
    </div>
  );
}

function Tickets() {
  const [tickets, setTickets] = useState([]);
  const [open, setOpen] = useState(false);
  const [active, setActive] = useState(null);
  const [form, setForm] = useState({ subject: "", body: "", priority: "normal" });
  const [reply, setReply] = useState("");
  const [busy, setBusy] = useState(false);
  const load = async () => setTickets((await api.get("/support/tickets")).data);
  useEffect(() => { load(); }, []);

  const create = async () => {
    setBusy(true);
    try { await api.post("/support/tickets", form); toast.success("Ticket opened — our team has been notified."); setOpen(false); setForm({ subject: "", body: "", priority: "normal" }); load(); }
    catch (e) { toast.error(apiError(e.response?.data?.detail)); } finally { setBusy(false); }
  };
  const sendReply = async () => {
    if (!reply.trim()) return;
    try { const { data } = await api.post(`/support/tickets/${active.id}/reply`, { body: reply }); setActive(data); setReply(""); load(); }
    catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };

  return (
    <div className="space-y-4">
      <div className="flex justify-end"><Button onClick={() => setOpen(true)} className="bg-emerald-500 text-[#062217] font-semibold" data-testid="new-ticket-btn"><Plus className="w-4 h-4 mr-1" /> New ticket</Button></div>
      {tickets.length === 0 ? (
        <div className="card-emerald p-10 text-center text-muted-foreground">No tickets yet. Open one and the QwikLogs team will reply.</div>
      ) : tickets.map((t) => (
        <button key={t.id} onClick={() => setActive(t)} className="w-full text-left card-emerald p-4 hover:border-emerald-500/40" data-testid={`ticket-${t.id}`}>
          <div className="flex items-center justify-between gap-2">
            <span className="font-semibold">{t.subject}</span>
            <Badge className={`${statusColor(t.status)} text-[10px]`}>{t.status.replace("_", " ")}</Badge>
          </div>
          <p className="text-xs text-muted-foreground mt-1">{t.messages?.length || 0} message(s) · updated {new Date(t.updated_at).toLocaleString()}</p>
        </button>
      ))}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="bg-[#0C110F] border-emerald-500/20" data-testid="new-ticket-dialog">
          <DialogHeader><DialogTitle>Open a support ticket</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div><Label>Subject</Label><Input value={form.subject} onChange={(e) => setForm({ ...form, subject: e.target.value })} className="mt-1.5 bg-[#0A0D0C] border-emerald-500/20" data-testid="ticket-subject-input" /></div>
            <div><Label>Describe the issue</Label><Textarea rows={4} value={form.body} onChange={(e) => setForm({ ...form, body: e.target.value })} className="mt-1.5 bg-[#0A0D0C] border-emerald-500/20" data-testid="ticket-body-input" /></div>
            <div><Label>Priority</Label>
              <select value={form.priority} onChange={(e) => setForm({ ...form, priority: e.target.value })} className="mt-1.5 w-full bg-[#0A0D0C] border border-emerald-500/20 rounded-md px-3 py-2 text-sm" data-testid="ticket-priority-select">
                <option value="low">Low</option><option value="normal">Normal</option><option value="high">High</option><option value="urgent">Urgent</option>
              </select>
            </div>
          </div>
          <DialogFooter><Button onClick={create} disabled={busy || !form.subject || !form.body} className="bg-emerald-500 text-[#062217] font-semibold" data-testid="ticket-submit-btn">{busy ? <Loader2 className="w-4 h-4 animate-spin" /> : "Open ticket"}</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!active} onOpenChange={(o) => !o && setActive(null)}>
        <DialogContent className="bg-[#0C110F] border-emerald-500/20 max-h-[85vh] overflow-y-auto" data-testid="ticket-detail-dialog">
          <DialogHeader><DialogTitle className="flex items-center gap-2">{active?.subject} <Badge className={`${statusColor(active?.status)} text-[10px]`}>{active?.status?.replace("_", " ")}</Badge></DialogTitle></DialogHeader>
          <div className="space-y-2">
            {active?.messages?.map((m, i) => (
              <div key={i} className={`rounded-lg p-3 text-sm ${m.from === "support" ? "bg-emerald-500/10 border border-emerald-500/20" : "bg-white/5"}`}>
                <p className="text-[11px] text-muted-foreground mb-1">{m.from === "support" ? "QwikLogs Support" : m.author} · {new Date(m.at).toLocaleString()}</p>
                <p className="whitespace-pre-wrap">{m.body}</p>
              </div>
            ))}
          </div>
          {active?.status !== "resolved" && (
            <div className="flex items-end gap-2 pt-2">
              <Textarea value={reply} onChange={(e) => setReply(e.target.value)} rows={1} placeholder="Reply…" className="bg-[#0A0D0C] border-emerald-500/20 resize-none" data-testid="ticket-reply-input" />
              <Button onClick={sendReply} className="bg-emerald-500 text-[#062217]" data-testid="ticket-reply-btn"><Send className="w-4 h-4" /></Button>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}

export default function Support() {
  return (
    <div className="space-y-6">
      <div className="flex items-center gap-2"><LifeBuoy className="w-5 h-5 text-emerald-400" /><h1 className="font-display text-2xl font-bold">Support</h1></div>
      <Tabs defaultValue="assistant">
        <TabsList className="bg-[#121816] border border-emerald-500/15">
          <TabsTrigger value="assistant" data-testid="tab-assistant"><Bot className="w-4 h-4 mr-1" /> AI Assistant</TabsTrigger>
          <TabsTrigger value="tickets" data-testid="tab-tickets"><MessageSquare className="w-4 h-4 mr-1" /> My tickets</TabsTrigger>
        </TabsList>
        <TabsContent value="assistant" className="mt-4"><Assistant /></TabsContent>
        <TabsContent value="tickets" className="mt-4"><Tickets /></TabsContent>
      </Tabs>
    </div>
  );
}
